import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import LeadCaptureModal from "components/LeadCaptureModal";
import { Heading, Text } from "components";

const sectionStyle = {
  maxWidth: 1120,
  margin: "0 auto",
  padding: "0 48px",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  fontFamily: "Manrope, sans-serif",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 999,
  padding: "6px 14px",
  background: "rgba(255,255,255,0.03)",
};

const cardStyle = {
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 24,
  background: "linear-gradient(160deg, rgba(16,16,22,0.95), rgba(8,8,12,0.98))",
  boxShadow: "0 18px 48px rgba(0,0,0,0.32)",
};

const scenarioSteps = [
  {
    step: "01",
    title: "WeChat entry",
    guest: "Guest gets a pre-arrival WeChat message with a voice CTA after booking.",
    system: "Reservation context, property rules, check-in window, and access instructions are preloaded.",
  },
  {
    step: "02",
    title: "Late arrival voice session",
    guest: "Guest arrives late, cannot find the right gate, and taps to talk instead of typing.",
    system: "AI confirms stay context, guides route and access, and adapts pace based on confusion and stress.",
  },
  {
    step: "03",
    title: "Structured follow-up",
    guest: "Guest receives entry details, Wi-Fi, and house rules in WeChat right after the call.",
    system: "The session is summarized and turned into persistent instructions, not a one-off voice exchange.",
  },
  {
    step: "04",
    title: "Issue routing",
    guest: "If the lock still fails or something breaks, the guest can report it without starting over.",
    system: "AI turns the issue into an operator-visible escalation with transcript, reason, and next action.",
  },
];

const moments = [
  {
    label: "Arrival stress",
    detail: "Voice matters most when guests are outside, tired, carrying luggage, and not in the mood to type.",
  },
  {
    label: "Family and older travelers",
    detail: "A direct conversation often works better than OTA chat threads or long instruction blocks.",
  },
  {
    label: "Operational handoff",
    detail: "The real value is not only answering questions. It is turning conversation into clean next actions.",
  },
];

const prototypeViews = [
  {
    id: "wechat",
    label: "WeChat Entry",
    title: "Pre-arrival message and one-tap voice entry",
    body: "This is the trust layer: reservation-aware message, QR-friendly entry, and a clear voice CTA before the guest ever gets stuck.",
  },
  {
    id: "voice",
    label: "H5 Voice Session",
    title: "Human-like AI front desk in a web voice session",
    body: "This is the active support layer: avatar, live transcript, quick actions, and fallback text inside a WeChat-launched H5 experience.",
  },
  {
    id: "ops",
    label: "Operator View",
    title: "Escalation, summary, and service-intelligence feedback",
    body: "This is the control layer: transcript, issue type, escalation reason, and service-quality signals instead of a dead-end chat log.",
  },
];

const journeyStates = [
  {
    id: "arrival",
    label: "Arrival",
    view: "wechat",
    title: "Guest receives the pre-arrival WeChat message",
    body: "The guest is on the way to the property and has one trusted place to start if anything goes wrong.",
    event: "WeChat pre-arrival message sent with reservation-aware voice CTA.",
    actionLabel: "Guest taps voice help",
  },
  {
    id: "access",
    label: "Access issue",
    view: "voice",
    title: "Guest starts a voice session after getting stuck at the wrong entrance",
    body: "The H5 session launches from WeChat. The AI front desk guides route correction, gate selection, and keypad entry in real time.",
    event: "AI detects confusion early and slows pacing instead of using a generic script.",
    actionLabel: "Resolve and send follow-up",
  },
  {
    id: "followup",
    label: "Follow-up",
    view: "wechat",
    title: "WeChat follow-up becomes the persistent instruction layer",
    body: "After the call, the guest gets the final entry instructions, Wi-Fi, house rules, and next-step contact options inside WeChat.",
    event: "Voice session summarized into structured guest-facing guidance.",
    actionLabel: "Escalate signage issue",
  },
  {
    id: "ops",
    label: "Operator view",
    view: "ops",
    title: "Operator sees summary, risk markers, and next actions",
    body: "The guest is now inside, but the property still has an operational problem: signage or map instructions may be causing repeated confusion.",
    event: "Service Intelligence flags a repeatable arrival problem for property-level QA.",
    actionLabel: "Restart journey",
  },
];

const scriptedConversation = {
  arrival: {
    guest: "I just arrived. Which entrance should I use?",
    ai: "Use the wooden gate beside the tea shop, not the glass lobby. If you want, tap voice help and I’ll walk you there.",
    hint: "The product should reduce hesitation before the guest gets stuck.",
  },
  access: {
    guest: "I’m at a keypad, but it says invalid code.",
    ai: "You’re close. Please check whether the keypad is next to Unit 3A. If yes, enter 2580 then press #. I’ll stay with you.",
    hint: "This is where real-time voice matters most: stress, confusion, hands full.",
  },
  followup: {
    guest: "I got in. Can you resend the Wi-Fi and checkout time?",
    ai: "Done. I’ve sent Wi-Fi, quiet hours, and checkout time in WeChat so you don’t need to remember it from the call.",
    hint: "Voice handles urgency; WeChat keeps the persistent record.",
  },
  ops: {
    guest: "Other guests may get confused here too.",
    ai: "Flagging this for the operator: likely signage or map issue causing repeat arrival confusion.",
    hint: "The interaction should become property-level insight, not disappear into a transcript.",
  },
};

function CtaLink({ href, children, secondary = false }) {
  return (
    <Link
      to={href}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.85rem 1.4rem",
        borderRadius: 14,
        textDecoration: "none",
        fontFamily: "Manrope, sans-serif",
        fontSize: "0.95rem",
        fontWeight: 600,
        color: "#fff",
        background: secondary
          ? "transparent"
          : "linear-gradient(135deg, #73d7ff, #5ed5b7 48%, #d98c5f)",
        border: secondary ? "1px solid rgba(255,255,255,0.14)" : "none",
      }}
    >
      {children}
    </Link>
  );
}

function BulletCard({ title, body }) {
  return (
    <div style={{ ...cardStyle, padding: "1.35rem 1.35rem 1.2rem" }}>
      <div
        style={{
          fontFamily: "Manrope, sans-serif",
          fontSize: "0.76rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "rgba(115,215,255,0.82)",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <Text
        size="s"
        as="p"
        style={{ color: "rgba(255,255,255,0.68)", lineHeight: 1.75 }}
      >
        {body}
      </Text>
    </div>
  );
}

function ScenarioStepCard({ step, title, guest, system }) {
  return (
    <div style={{ ...cardStyle, padding: "1.35rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 999,
            background: "linear-gradient(135deg, #73d7ff, #d98c5f)",
            color: "#041018",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Manrope, sans-serif",
            fontSize: "0.86rem",
            fontWeight: 700,
            letterSpacing: "0.03em",
          }}
        >
          {step}
        </div>
        <div
          style={{
            fontFamily: "Franie, serif",
            fontSize: "1.25rem",
            color: "#f4f0eb",
          }}
        >
          {title}
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <div
            style={{
              fontFamily: "Manrope, sans-serif",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(115,215,255,0.82)",
              marginBottom: 6,
            }}
          >
            Guest experience
          </div>
          <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>
            {guest}
          </Text>
        </div>

        <div>
          <div
            style={{
              fontFamily: "Manrope, sans-serif",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(217,140,95,0.9)",
              marginBottom: 6,
            }}
          >
            AI front desk + service intelligence
          </div>
          <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.58)", lineHeight: 1.7 }}>
            {system}
          </Text>
        </div>
      </div>
    </div>
  );
}

function ViewToggle({ activeView, onChange }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {prototypeViews.map((view) => (
        <button
          key={view.id}
          type="button"
          onClick={() => onChange(view.id)}
          style={{
            padding: "0.75rem 1rem",
            borderRadius: 999,
            border: activeView === view.id
              ? "1px solid rgba(115,215,255,0.9)"
              : "1px solid rgba(255,255,255,0.12)",
            background: activeView === view.id
              ? "linear-gradient(135deg, rgba(115,215,255,0.18), rgba(217,140,95,0.14))"
              : "rgba(255,255,255,0.03)",
            color: "#fff",
            cursor: "pointer",
            fontFamily: "Manrope, sans-serif",
            fontSize: "0.86rem",
            fontWeight: 700,
          }}
        >
          {view.label}
        </button>
      ))}
    </div>
  );
}

function PhoneFrame({ children }) {
  return (
    <div
      style={{
        ...cardStyle,
        maxWidth: 360,
        margin: "0 auto",
        padding: 12,
        borderRadius: 28,
        background: "linear-gradient(180deg, rgba(18,18,24,0.98), rgba(6,6,10,0.98))",
      }}
    >
      <div
        style={{
          borderRadius: 22,
          minHeight: 620,
          overflow: "hidden",
          background: "linear-gradient(180deg, #f4efe8 0%, #efe6db 100%)",
          position: "relative",
        }}
      >
        <div
          style={{
            width: 92,
            height: 20,
            borderRadius: 999,
            background: "#0c0d11",
            margin: "12px auto 0",
          }}
        />
        {children}
      </div>
    </div>
  );
}

function PrototypeWechatView() {
  return (
    <PhoneFrame>
      <div style={{ padding: "18px 16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontWeight: 800, color: "#1f3d2e" }}>AI Front Desk</div>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.78rem", color: "#5d7865" }}>WeChat service</div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ justifySelf: "start", maxWidth: "88%", background: "#ffffff", borderRadius: 18, padding: "12px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.86rem", color: "#304136", lineHeight: 1.6 }}>
              Hi Lin. Welcome to Hidden Pine Stay.
              <br />
              Check-in tonight: <strong>9:00 PM onward</strong>.
              <br />
              Need help finding the property or getting in?
            </div>
          </div>

          <div style={{ justifySelf: "start", maxWidth: "88%", background: "#d7f6df", borderRadius: 18, padding: "12px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.83rem", color: "#214534", lineHeight: 1.55 }}>
              <strong>Quick actions</strong>
              <br />
              • Talk to AI front desk
              <br />
              • View check-in instructions
              <br />
              • Parking and map
            </div>
          </div>

          <div style={{ justifySelf: "center", width: 180, height: 180, borderRadius: 24, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 28px rgba(0,0,0,0.08)" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#68806d", marginBottom: 10 }}>
                Scan To Open
              </div>
              <div style={{ width: 112, height: 112, margin: "0 auto", borderRadius: 12, background: "repeating-linear-gradient(90deg, #142c1f 0 8px, #e8f3ea 8px 16px), repeating-linear-gradient(180deg, #142c1f 0 8px, #e8f3ea 8px 16px)" }} />
            </div>
          </div>
        </div>

        <div style={{ position: "absolute", left: 16, right: 16, bottom: 18 }}>
          <button
            type="button"
            style={{
              width: "100%",
              padding: "0.95rem 1rem",
              borderRadius: 16,
              border: "none",
              background: "linear-gradient(135deg, #4ac27f, #1f9f62)",
              color: "#fff",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Talk to AI front desk
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
}

function PrototypeVoiceView({ conversation }) {
  return (
    <PhoneFrame>
      <div style={{ padding: "22px 18px 20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.76rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#6a706d" }}>
            Live Voice Session
          </div>
          <div style={{ marginTop: 12, width: 112, height: 112, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, #8fe5ff, #2d7eb8 56%, #17222f 100%)", marginInline: "auto", boxShadow: "0 18px 44px rgba(30,95,150,0.3)" }} />
          <div style={{ fontFamily: "Franie, serif", fontSize: "1.45rem", color: "#17222f", marginTop: 14 }}>
            Mei, AI Front Desk
          </div>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.86rem", color: "#69716d", marginTop: 4 }}>
            Helping with arrival, access, and stay support
          </div>
        </div>

        <div style={{ ...cardStyle, marginTop: 18, padding: "1rem", borderRadius: 18, background: "rgba(255,255,255,0.78)" }}>
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#4f655e", marginBottom: 8 }}>
            Live transcript
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ background: "#f2f5f3", borderRadius: 14, padding: "10px 12px", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "#2b3530", lineHeight: 1.55 }}>
              <strong>Guest:</strong> {conversation.guest}
            </div>
            <div style={{ background: "#dff6e6", borderRadius: 14, padding: "10px 12px", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "#214534", lineHeight: 1.55 }}>
              <strong>AI:</strong> {conversation.ai}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginTop: 14 }}>
          {["Parking", "Wi-Fi", "Call human"].map((label) => (
            <div key={label} style={{ borderRadius: 14, padding: "10px 8px", textAlign: "center", background: "rgba(255,255,255,0.78)", fontFamily: "Manrope, sans-serif", fontSize: "0.78rem", fontWeight: 700, color: "#2e3834" }}>
              {label}
            </div>
          ))}
        </div>

        <div style={{ position: "absolute", left: 18, right: 18, bottom: 20, display: "grid", gap: 10 }}>
          <div style={{ textAlign: "center", fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", color: "#61716a" }}>
            Listening • reservation loaded • fallback text available
          </div>
          <div style={{ width: 84, height: 84, borderRadius: "50%", margin: "0 auto", background: "linear-gradient(135deg, #73d7ff, #d98c5f)", display: "flex", alignItems: "center", justifyContent: "center", color: "#071118", fontFamily: "Manrope, sans-serif", fontSize: "1.7rem", fontWeight: 900, boxShadow: "0 18px 44px rgba(115,215,255,0.28)" }}>
            ●
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function PrototypeOpsView() {
  return (
    <div style={{ ...cardStyle, padding: "1.4rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(240px, 0.8fr)", gap: 16 }} className="md:grid-cols-1">
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 6 }}>
              Session summary
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ fontFamily: "Franie, serif", fontSize: "1.2rem", color: "#f4f0eb", marginBottom: 8 }}>
                Hidden Pine Stay · Unit 3A
              </div>
              <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
                Guest reached the wrong entrance at 9:42 PM. AI resolved route confusion,
                guided keypad entry, and sent WeChat follow-up with Wi-Fi and check-out rules.
              </Text>
            </div>
          </div>

          <div>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 6 }}>
              Service intelligence
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }} className="sm:grid-cols-1">
              {[
                { label: "Confusion", value: "High at start" },
                { label: "Resolution", value: "No human takeover" },
                { label: "Risk", value: "Low after follow-up" },
              ].map((item) => (
                <div key={item.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: "12px 12px 10px" }}>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.42)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {item.label}
                  </div>
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.95rem", fontWeight: 800, color: "#fff", marginTop: 6 }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 18, padding: "14px 14px 12px" }}>
            <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 10 }}>
              Operator actions
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {["Review transcript", "Mark property signage issue", "Update check-in instructions"].map((item) => (
                <div key={item} style={{ borderRadius: 14, padding: "10px 12px", background: "rgba(255,255,255,0.05)", fontFamily: "Manrope, sans-serif", fontSize: "0.84rem", color: "rgba(255,255,255,0.74)" }}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "linear-gradient(135deg, rgba(115,215,255,0.12), rgba(217,140,95,0.12))", borderRadius: 18, padding: "14px 14px 12px", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontFamily: "Franie, serif", fontSize: "1.08rem", color: "#f4f0eb", marginBottom: 8 }}>
              Operational insight
            </div>
            <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.7 }}>
              If this arrival pattern repeats across the same property, the system should
              flag signage, map pins, or entry instructions as a service-quality issue.
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VoiceHostDemoPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeView, setActiveView] = useState("wechat");
  const [journeyIndex, setJourneyIndex] = useState(0);
  const [conversationTurn, setConversationTurn] = useState(0);
  const activeMeta = useMemo(
    () => prototypeViews.find((view) => view.id === activeView) || prototypeViews[0],
    [activeView],
  );
  const journeyState = journeyStates[journeyIndex];
  const conversation = scriptedConversation[journeyState.id];

  const handleAdvanceJourney = () => {
    if (journeyIndex === journeyStates.length - 1) {
      setJourneyIndex(0);
      setActiveView(journeyStates[0].view);
      setConversationTurn(0);
      return;
    }
    const nextIndex = journeyIndex + 1;
    setJourneyIndex(nextIndex);
    setActiveView(journeyStates[nextIndex].view);
    setConversationTurn(0);
  };

  return (
    <>
      <Helmet>
        <title>Voice Front Desk Demo — AIHumanity</title>
        <meta
          name="description"
          content="Preview AIHumanity's voice-first AI front desk for homestays and serviced apartments: WeChat entry, voice check-in support, issue triage, and service intelligence."
        />
      </Helmet>

      <div className="w-full bg-black-900_02">
        <TopMenuBar />

        <section
          style={{
            paddingTop: 124,
            paddingBottom: 84,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 12% 20%, rgba(115,215,255,0.15), transparent 34%), radial-gradient(circle at 82% 16%, rgba(217,140,95,0.16), transparent 28%), linear-gradient(180deg, rgba(5,6,8,0.85), rgba(5,6,8,1))",
              pointerEvents: "none",
            }}
          />

          <div style={{ ...sectionStyle, position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, 0.9fr)",
                gap: 28,
                alignItems: "center",
              }}
              className="md:grid-cols-1"
            >
              <div>
                <span style={badgeStyle}>Voice Front Desk Demo</span>
                <Heading
                  size="3xl"
                  as="h1"
                  className="!text-gray-300 mt-5"
                  style={{ maxWidth: 720, lineHeight: "1.03" }}
                >
                  The demo path for a WeChat-first AI front desk.
                </Heading>
                <Text
                  size="md"
                  as="p"
                  style={{
                    maxWidth: 700,
                    marginTop: 18,
                    color: "rgba(255,255,255,0.66)",
                    lineHeight: 1.8,
                  }}
                >
                  This page frames one believable operating story: a guest arrives
                  late, needs help getting into the property, speaks to a human-like
                  AI front desk by voice, gets a structured WeChat follow-up, and
                  only triggers staff involvement if the issue actually needs it.
                </Text>

                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 28 }}>
                  <CtaLink href="/demo/wechat-front-desk">Open the prototype flow</CtaLink>
                  <CtaLink href="/use-cases/ai-front-desk-for-homestays" secondary>
                    Read the use-case page
                  </CtaLink>
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    style={{
                      padding: "0.85rem 1.4rem",
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                      color: "#fff",
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Talk about a pilot
                  </button>
                </div>
              </div>

              <div style={{ ...cardStyle, padding: "1.5rem" }}>
                <div
                  style={{
                    fontFamily: "Franie, serif",
                    fontSize: "1.8rem",
                    color: "#f4f0eb",
                    marginBottom: 16,
                  }}
                >
                  Hero scenario
                </div>
                <div style={{ display: "grid", gap: 12 }}>
                  {[
                    "9:42 PM: guest lands at the wrong building entrance.",
                    "The guest taps a WeChat link instead of typing inside a long chat thread.",
                    "The voice host confirms the reservation, guides the right gate, and walks through the lock flow.",
                    "A follow-up WeChat card sends Wi-Fi, house rules, and the final entry instructions.",
                    "If access still fails, the operator gets a clean escalation with reason, transcript, and next step.",
                  ].map((line) => (
                    <div
                      key={line}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "26px minmax(0, 1fr)",
                        gap: 12,
                        alignItems: "start",
                      }}
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 999,
                          background: "linear-gradient(135deg, #73d7ff, #d98c5f)",
                          color: "#041018",
                          fontFamily: "Manrope, sans-serif",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        •
                      </div>
                      <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>
                        {line}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ paddingBottom: 84 }}>
          <div style={sectionStyle}>
            <div style={{ marginBottom: 24 }}>
              <span style={badgeStyle}>Prototype Flow</span>
              <Heading
                size="lg"
                as="h2"
                className="!text-gray-300 mt-4"
                style={{ maxWidth: 760, lineHeight: "1.08" }}
              >
                Three screens that define the WeChat and H5 product shape.
              </Heading>
              <Text
                size="md"
                as="p"
                style={{ marginTop: 14, color: "rgba(255,255,255,0.66)", lineHeight: 1.8, maxWidth: 760 }}
              >
                The prototype should prove one complete loop: trusted WeChat entry,
                rich H5 voice support, and an operator view that turns interaction into action.
              </Text>
            </div>

            <div
              style={{
                ...cardStyle,
                padding: "1.35rem",
                marginBottom: 18,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 0.9fr)",
                gap: 18,
                alignItems: "start",
              }}
              className="md:grid-cols-1"
            >
              <div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  {journeyStates.map((state, index) => (
                    <button
                      key={state.id}
                      type="button"
                      onClick={() => {
                        setJourneyIndex(index);
                        setActiveView(state.view);
                        setConversationTurn(0);
                      }}
                      style={{
                        padding: "0.65rem 0.9rem",
                        borderRadius: 999,
                        border: index === journeyIndex
                          ? "1px solid rgba(217,140,95,0.95)"
                          : "1px solid rgba(255,255,255,0.12)",
                        background: index === journeyIndex
                          ? "linear-gradient(135deg, rgba(217,140,95,0.18), rgba(115,215,255,0.14))"
                          : "rgba(255,255,255,0.03)",
                        color: "#fff",
                        cursor: "pointer",
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      {state.label}
                    </button>
                  ))}
                </div>

                <div style={{ fontFamily: "Franie, serif", fontSize: "1.35rem", color: "#f4f0eb" }}>
                  {journeyState.title}
                </div>
                <Text
                  size="s"
                  as="p"
                  style={{ marginTop: 10, color: "rgba(255,255,255,0.68)", lineHeight: 1.75, maxWidth: 640 }}
                >
                  {journeyState.body}
                </Text>
              </div>

              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 18, padding: "14px 16px" }}>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 8 }}>
                  Journey event
                </div>
                <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.74)", lineHeight: 1.75 }}>
                  {journeyState.event}
                </Text>
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 14,
                    padding: "12px 12px 10px",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(115,215,255,0.82)", marginBottom: 6 }}>
                    Mock conversation beat
                  </div>
                  <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.75 }}>
                    {conversationTurn === 0 ? conversation.guest : conversation.ai}
                  </Text>
                  <button
                    type="button"
                    onClick={() => setConversationTurn((current) => (current === 0 ? 1 : 0))}
                    style={{
                      marginTop: 10,
                      padding: "0.6rem 0.8rem",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.03)",
                      color: "#fff",
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {conversationTurn === 0 ? "Show AI reply" : "Show guest line"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleAdvanceJourney}
                  style={{
                    marginTop: 14,
                    width: "100%",
                    padding: "0.85rem 1rem",
                    borderRadius: 14,
                    border: "none",
                    background: "linear-gradient(135deg, #73d7ff, #5ed5b7 48%, #d98c5f)",
                    color: "#fff",
                    fontFamily: "Manrope, sans-serif",
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  {journeyState.actionLabel}
                </button>
              </div>
            </div>

            <div
              style={{
                ...cardStyle,
                padding: "1.5rem",
                marginBottom: 18,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.9fr)",
                gap: 20,
                alignItems: "start",
              }}
              className="md:grid-cols-1"
            >
              <div>
                <ViewToggle activeView={activeView} onChange={setActiveView} />
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontFamily: "Franie, serif", fontSize: "1.35rem", color: "#f4f0eb" }}>
                    {activeMeta.title}
                  </div>
                  <Text
                    size="s"
                    as="p"
                    style={{ marginTop: 10, color: "rgba(255,255,255,0.68)", lineHeight: 1.75, maxWidth: 560 }}
                  >
                    {activeMeta.body}
                  </Text>
                </div>
              </div>

              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 18, padding: "14px 16px" }}>
                <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 8 }}>
                  V1 principle
                </div>
                <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.75 }}>
                  Keep WeChat as the trusted entry point and persistence layer. Use H5 for
                  richer voice UI and iteration speed. Keep the operator view focused on
                  summary, escalation, and service-quality signals.
                </Text>
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 14,
                    padding: "12px 12px 10px",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(217,140,95,0.9)", marginBottom: 6 }}>
                    Prototype note
                  </div>
                  <Text size="s" as="p" style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.75 }}>
                    {conversation.hint}
                  </Text>
                </div>
              </div>
            </div>

            {activeView === "wechat" && <PrototypeWechatView />}
            {activeView === "voice" && <PrototypeVoiceView conversation={conversation} />}
            {activeView === "ops" && <PrototypeOpsView />}
          </div>
        </section>

        <section style={{ paddingBottom: 84 }}>
          <div style={sectionStyle}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 18,
              }}
              className="md:grid-cols-1"
            >
              <BulletCard
                title="Interaction Layer"
                body="The guest-facing product: WeChat entry, voice support, text fallback, check-in help, issue intake, and escalation."
              />
              <BulletCard
                title="Service Intelligence"
                body="The operator-facing layer: frustration and confusion markers, session summaries, service-quality feedback, and repeat issue detection."
              />
              <BulletCard
                title="Why This Page Exists"
                body="It keeps the site architecture coherent. Hospitality becomes a vertical application with its own story, without pretending the whole site is already a rental software brand."
              />
            </div>
          </div>
        </section>

        <section style={{ paddingBottom: 84 }}>
          <div style={sectionStyle}>
            <div style={{ marginBottom: 24 }}>
              <span style={badgeStyle}>Scenario Walkthrough</span>
              <Heading
                size="lg"
                as="h2"
                className="!text-gray-300 mt-4"
                style={{ maxWidth: 760, lineHeight: "1.08" }}
              >
                One end-to-end story that proves the product is operational, not conceptual.
              </Heading>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 18,
              }}
              className="md:grid-cols-1"
            >
              {scenarioSteps.map((item) => (
                <ScenarioStepCard key={item.step} {...item} />
              ))}
            </div>
          </div>
        </section>

        <section style={{ paddingBottom: 84 }}>
          <div style={sectionStyle}>
            <div
              style={{
                ...cardStyle,
                padding: "1.8rem",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) minmax(280px, 0.9fr)",
                gap: 22,
                alignItems: "start",
              }}
              className="md:grid-cols-1"
            >
              <div>
                <span style={badgeStyle}>Why Voice Wins Here</span>
                <Heading
                  size="lg"
                  as="h2"
                  className="!text-gray-300 mt-4"
                  style={{ lineHeight: "1.08", maxWidth: 620 }}
                >
                  This category earns trust in moments when typing is the wrong interface.
                </Heading>
                <Text
                  size="md"
                  as="p"
                  style={{
                    marginTop: 16,
                    color: "rgba(255,255,255,0.66)",
                    lineHeight: 1.8,
                    maxWidth: 650,
                  }}
                >
                  The strongest moments are not abstract concierge use cases. They are
                  late arrival, entry confusion, family travel, and in-stay problems where
                  a real-time voice interaction feels like a front desk instead of a bot.
                </Text>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                {moments.map((item) => (
                  <BulletCard key={item.label} title={item.label} body={item.detail} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section style={{ paddingBottom: 96 }}>
          <div style={sectionStyle}>
            <div
              style={{
                ...cardStyle,
                padding: "2rem",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 360px)",
                gap: 24,
                alignItems: "stretch",
              }}
              className="md:grid-cols-1"
            >
              <div>
                <span style={badgeStyle}>Where This Leads</span>
                <Heading
                  size="lg"
                  as="h2"
                  className="!text-gray-300 mt-4"
                  style={{ lineHeight: "1.08", maxWidth: 640 }}
                >
                  Use this page as the bridge into the WeChat and H5 prototype.
                </Heading>
                <Text
                  size="md"
                  as="p"
                  style={{
                    marginTop: 16,
                    color: "rgba(255,255,255,0.66)",
                    lineHeight: 1.8,
                    maxWidth: 670,
                  }}
                >
                  The next product step is not a full booking platform. It is a
                  prototype that proves one flow well: WeChat entry, H5 voice session,
                  operator visibility, structured WeChat follow-up, and escalation only
                  when confidence or workflow boundaries require it.
                </Text>
              </div>

              <div style={{ ...cardStyle, padding: "1.4rem" }}>
                <div
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontSize: "0.76rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(217,140,95,0.9)",
                    marginBottom: 12,
                  }}
                >
                  Recommended next clicks
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <CtaLink href="/demo/wechat-front-desk">Try the WeChat / H5 prototype</CtaLink>
                  <CtaLink href="/use-cases/ai-front-desk-for-homestays" secondary>
                    Review the market/use-case fit
                  </CtaLink>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterSection />

        <LeadCaptureModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          kind="contact"
          context="Interested in: AI Front Desk pilot"
        />
      </div>
    </>
  );
}
